/**
 * Portal de Cotizaciones Corporativas (PCC) — Módulo real productivo.
 *
 * Página única con secciones internas controladas por la URL:
 *   /pcc                  → Dashboard
 *   /pcc/padron           → Padrón de Vehículos
 *   /pcc/cotizaciones     → Listado de cotizaciones (Fase 2)
 *   /pcc/configuracion    → Configuración (admin): Aseguradoras/Coberturas/Tasas
 *
 * Acceso controlado por el sistema de permisos estándar (menús pcc_*).
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, Car, FileText, Settings, ShieldCheck, ShieldAlert, Percent,
  Plus, Pencil, Trash2, Search, Upload, Loader2, Clock, AlertCircle,
  BarChart3, TrendingUp, Users, FileCheck2, CheckCircle2, Eye, Download, Star,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIPO_OPTIONS = ["Empleado", "Flotilla", "Personal del banco"];
const SEGMENTO_OPTIONS = ["Empleado", "Flotilla", "Personal del banco"];

export default function PCCPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const isAdmin = ["admin", "administrador", "superadmin"].includes(role);
  // Rol ALNAP: vista restringida — solo ve Padrón y Cotizaciones
  const isAlnap = role === "alnap";
  const active = section || (isAlnap ? "padron" : "dashboard");

  const tabs = isAlnap
    ? [
        { id: "padron", label: "Datos de cliente", icon: Car, path: "/pcc/padron" },
        { id: "cotizaciones", label: "Cotizaciones", icon: FileText, path: "/pcc/cotizaciones" },
      ]
    : [
        { id: "dashboard", label: "Dashboard", icon: BarChart3, path: "/pcc" },
        { id: "padron", label: "Datos de cliente", icon: Car, path: "/pcc/padron" },
        { id: "cotizaciones", label: "Cotizaciones", icon: FileText, path: "/pcc/cotizaciones" },
        ...(isAdmin ? [{ id: "configuracion", label: "Configuración", icon: Settings, path: "/pcc/configuracion" }] : []),
      ];

  // Guard: si un usuario ALNAP intenta entrar por URL a una pestaña restringida,
  // lo redirigimos a Padrón (su landing por defecto).
  const allowedSections = tabs.map((t) => t.id);
  const safeActive = allowedSections.includes(active) ? active : (isAlnap ? "padron" : "dashboard");

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 overflow-x-hidden max-w-full" data-testid="pcc-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#193966]" />
            Portal de Cotizaciones Corporativas
          </h1>
          <p className="text-xs text-slate-500 mt-1">ALNAP · Asociación La Nacional de Ahorros y Préstamos</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto" data-testid="pcc-tabs">
        {tabs.map((t) => {
          const Ic = t.icon;
          const sel = safeActive === t.id;
          return (
            <button
              key={t.id}
              onClick={() => navigate(t.path)}
              data-testid={`pcc-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                sel ? "border-[#80BC00] text-[#193966]" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Ic className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {safeActive === "dashboard" && !isAlnap && <DashboardSection />}
      {safeActive === "padron" && <PadronSection isAdmin={isAdmin} />}
      {safeActive === "cotizaciones" && <CotizacionesSection isAdmin={isAdmin} />}
      {safeActive === "configuracion" && isAdmin && <ConfiguracionSection />}
      </main>
    </div>
  );
}

// ============================================================================
// DASHBOARD
// ============================================================================
function DashboardSection() {
  const { getAuthHeaders } = useAuth();
  const [kpis, setKpis] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/pcc/dashboard`, getAuthHeaders())
      .then(r => r.json()).then(setKpis).catch(() => toast.error("Error al cargar dashboard"));
  }, [getAuthHeaders]);
  const cards = [
    { label: "Aseguradoras activas", value: kpis?.aseguradoras_activas ?? "—", icon: ShieldCheck, color: "from-blue-500 to-indigo-600" },
    { label: "Vehículos en padrón", value: kpis?.vehiculos_activos ?? "—", icon: Car, color: "from-emerald-500 to-green-600" },
    { label: "Tasas vigentes", value: kpis?.tasas_vigentes ?? "—", icon: Percent, color: "from-amber-500 to-orange-600" },
    { label: "Cotizaciones del mes", value: kpis?.cotizaciones_mes ?? "—", icon: FileText, color: "from-purple-500 to-fuchsia-600" },
    { label: "Firmadas del mes", value: kpis?.firmadas_mes ?? "—", icon: FileCheck2, color: "from-rose-500 to-pink-600" },
    { label: "Prima total gestionada", value: kpis?.prima_total_gestionada ? `DOP ${Number(kpis.prima_total_gestionada).toLocaleString("es-DO")}` : "—", icon: TrendingUp, color: "from-slate-700 to-slate-900" },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((c) => {
          const Ic = c.icon;
          return (
            <Card key={c.label} className={`border-0 shadow-md bg-gradient-to-br ${c.color} text-white`}>
              <CardContent className="p-4">
                <div className="flex justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">{c.label}</p>
                  <Ic className="w-4 h-4 opacity-80" />
                </div>
                <p className="text-2xl font-bold">{c.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-6 text-center text-slate-500 text-sm">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-semibold text-slate-700">Fase 1 — Configuración y Padrón</p>
          <p className="text-xs mt-1">La generación de cotizaciones, el portal del empleado y la firma digital se habilitarán en las siguientes fases del proyecto.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// PADRÓN
// ============================================================================
function PadronSection({ isAdmin }) {  // eslint-disable-line no-unused-vars
  const { getAuthHeaders } = useAuth();
  // ── Catálogos ──
  const [catalogoTipos, setCatalogoTipos] = useState([]); // [{nombre, marcas:[{nombre, modelos:[]}]}]
  const [aseguradorasPrincipales, setAseguradorasPrincipales] = useState([]);

  // ── Formulario ──
  const todayIso = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    fecha_cotizacion: todayIso,
    empleado_nombre: "",
    cedula: "",
    tipo_vehiculo: "",
    marca: "",
    modelo: "",
    anio: "",
    placa: "",
    chasis: "",
    color: "",
    valor_asegurado: "",
  });
  const [planesSel, setPlanesSel] = useState({}); // {plan_id: true/false}
  const [aseguradorasAbiertas, setAseguradorasAbiertas] = useState({}); // {aseg_id: true/false}
  const [creating, setCreating] = useState(false);

  // ── Histórico ──
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loadingCot, setLoadingCot] = useState(false);
  const [verCotId, setVerCotId] = useState(null);

  // Carga inicial: catálogos y cotizaciones recientes
  useEffect(() => {
    fetch(`${API_URL}/api/pcc/catalogo-vehiculos`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setCatalogoTipos(d.tipos || []))
      .catch(() => toast.error("No se pudo cargar el catálogo de vehículos"));
    fetch(`${API_URL}/api/pcc/aseguradoras-principales`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => {
        const items = d.items || [];
        setAseguradorasPrincipales(items);
        // Por defecto: todas las aseguradoras "abiertas" (visible su lista de planes)
        setAseguradorasAbiertas(Object.fromEntries(items.map((a) => [a.id, true])));
      })
      .catch(() => toast.error("No se pudo cargar las aseguradoras"));
  }, [getAuthHeaders]);

  const fetchCotizaciones = useCallback(async () => {
    setLoadingCot(true);
    try {
      const r = await fetch(`${API_URL}/api/pcc/cotizaciones?page=1&page_size=5`, getAuthHeaders());
      const d = await r.json();
      setCotizaciones(d.items || []);
    } catch {
      // No bloquea el flujo principal
    }
    setLoadingCot(false);
  }, [getAuthHeaders]);
  useEffect(() => { fetchCotizaciones(); }, [fetchCotizaciones]);

  // ── Cascada Tipo → Marca → Modelo ──
  const tipoActivo = catalogoTipos.find((t) => t.nombre === form.tipo_vehiculo);
  const marcasDisponibles = tipoActivo ? tipoActivo.marcas : [];
  const marcaActiva = marcasDisponibles.find((m) => m.nombre === form.marca);
  const modelosDisponibles = marcaActiva ? marcaActiva.modelos : [];

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Reset cascadas dependientes si cambia el padre
      if (key === "tipo_vehiculo") { next.marca = ""; next.modelo = ""; }
      if (key === "marca") { next.modelo = ""; }
      return next;
    });
  };

  // ── Aseguradoras / Planes ──
  const togglePlan = (planId, checked) => {
    setPlanesSel((prev) => ({ ...prev, [planId]: checked }));
  };
  const toggleAseguradoraCompleta = (aseg, checked) => {
    const updates = {};
    (aseg.planes || []).forEach((p) => { updates[p.id] = checked; });
    setPlanesSel((prev) => ({ ...prev, ...updates }));
  };
  const planesAseguradoraTodos = (aseg) =>
    (aseg.planes || []).length > 0 && (aseg.planes || []).every((p) => planesSel[p.id]);
  const planesAseguradoraAlguno = (aseg) =>
    (aseg.planes || []).some((p) => planesSel[p.id]);
  const totalPlanesSeleccionados = Object.values(planesSel).filter(Boolean).length;

  // ── Submit ──
  const handleGenerar = async () => {
    // Validaciones mínimas
    if (!form.empleado_nombre.trim()) { toast.error("Ingrese el nombre del cliente"); return; }
    if (!form.cedula.trim()) { toast.error("Ingrese la cédula"); return; }
    if (!form.tipo_vehiculo) { toast.error("Seleccione el tipo de vehículo"); return; }
    if (!form.marca) { toast.error("Seleccione la marca"); return; }
    if (!form.modelo) { toast.error("Seleccione el modelo"); return; }
    if (!form.anio) { toast.error("Ingrese el año"); return; }
    if (!form.valor_asegurado || Number(form.valor_asegurado) <= 0) {
      toast.error("Ingrese el valor asegurado");
      return;
    }
    const planesIds = Object.keys(planesSel).filter((k) => planesSel[k]);
    if (planesIds.length === 0) {
      toast.error("Seleccione al menos una aseguradora/plan para cotizar");
      return;
    }
    setCreating(true);
    try {
      // 1) Crear el vehículo (Padrón)
      const vehPayload = {
        empleado_nombre: form.empleado_nombre.trim(),
        cedula: form.cedula.trim(),
        email: "",
        marca: form.marca,
        modelo: form.modelo,
        anio: Number(form.anio),
        chasis: form.chasis.trim(),
        placa: form.placa.trim(),
        valor_asegurado: Number(form.valor_asegurado),
        tipo: "Empleado",  // segmento por defecto
        fecha_cotizacion: form.fecha_cotizacion || todayIso,
        tipo_vehiculo: form.tipo_vehiculo,
        color: form.color.trim(),
        activo: true,
      };
      const rv = await fetch(`${API_URL}/api/pcc/padron`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(vehPayload),
      });
      const veh = await rv.json();
      if (!rv.ok) throw new Error(veh.detail || "No se pudo guardar el vehículo");

      // 2) Generar la cotización con los planes seleccionados
      const rc = await fetch(`${API_URL}/api/pcc/cotizaciones`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          vehiculo_id: veh.id,
          planes_ids: planesIds,
          vigencia_dias: 30,
        }),
      });
      const cot = await rc.json();
      if (!rc.ok) throw new Error(cot.detail || "No se pudo generar la cotización");

      toast.success("Cotización generada");
      // Reset formulario (mantiene fecha)
      setForm({
        fecha_cotizacion: todayIso,
        empleado_nombre: "", cedula: "", tipo_vehiculo: "", marca: "", modelo: "",
        anio: "", placa: "", chasis: "", color: "", valor_asegurado: "",
      });
      setPlanesSel({});
      setVerCotId(cot.id);
      fetchCotizaciones();
    } catch (err) {
      toast.error(err.message || "Error al generar cotización");
    }
    setCreating(false);
  };

  return (
    <div className="space-y-5">
      {/* === FORMULARIO DE COTIZACIÓN === */}
      <Card className="border-slate-200" data-testid="padron-cotizar-form">
        <CardHeader className="py-3 px-4 border-b bg-gradient-to-r from-[#193966] to-[#0F2240] text-white">
          <div className="flex items-center gap-2">
            <FileCheck2 className="w-5 h-5" />
            <CardTitle className="text-sm font-bold">Nueva cotización corporativa</CardTitle>
          </div>
          <p className="text-[11px] text-blue-100 mt-1">
            Complete los datos del cliente, el vehículo y seleccione las aseguradoras con las que desea comparar.
          </p>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {/* Sección 1: Cliente */}
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Datos del cliente
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormField label="Fecha" required>
                <Input
                  type="date"
                  value={form.fecha_cotizacion}
                  onChange={(e) => setField("fecha_cotizacion", e.target.value)}
                  className="h-9"
                  data-testid="cot-fecha"
                />
              </FormField>
              <FormField label="Nombre del cliente" required className="md:col-span-2">
                <Input
                  value={form.empleado_nombre}
                  onChange={(e) => setField("empleado_nombre", e.target.value)}
                  placeholder="Ej: Juan Pérez Rodríguez"
                  className="h-9"
                  data-testid="cot-cliente"
                />
              </FormField>
              <FormField label="Cédula" required>
                <Input
                  value={form.cedula}
                  onChange={(e) => setField("cedula", e.target.value)}
                  placeholder="001-1234567-9"
                  className="h-9"
                  data-testid="cot-cedula"
                />
              </FormField>
            </div>
          </div>

          {/* Sección 2: Vehículo */}
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1.5">
              <Car className="w-3.5 h-3.5" /> Datos del vehículo
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormField label="Tipo de vehículo" required>
                <select
                  value={form.tipo_vehiculo}
                  onChange={(e) => setField("tipo_vehiculo", e.target.value)}
                  className="h-9 w-full border border-slate-300 rounded px-2 text-sm bg-white"
                  data-testid="cot-tipo-vehiculo"
                >
                  <option value="">— Seleccione —</option>
                  {catalogoTipos.map((t) => (
                    <option key={t.nombre} value={t.nombre}>{t.nombre}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Marca" required>
                <select
                  value={form.marca}
                  onChange={(e) => setField("marca", e.target.value)}
                  disabled={!form.tipo_vehiculo}
                  className="h-9 w-full border border-slate-300 rounded px-2 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400"
                  data-testid="cot-marca"
                >
                  <option value="">{form.tipo_vehiculo ? "— Seleccione —" : "Elija primero el tipo"}</option>
                  {marcasDisponibles.map((m) => (
                    <option key={m.nombre} value={m.nombre}>{m.nombre}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Modelo" required>
                <select
                  value={form.modelo}
                  onChange={(e) => setField("modelo", e.target.value)}
                  disabled={!form.marca}
                  className="h-9 w-full border border-slate-300 rounded px-2 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400"
                  data-testid="cot-modelo"
                >
                  <option value="">{form.marca ? "— Seleccione —" : "Elija primero la marca"}</option>
                  {modelosDisponibles.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Año" required>
                <Input
                  type="number" min={1980} max={new Date().getFullYear() + 2}
                  value={form.anio}
                  onChange={(e) => setField("anio", e.target.value)}
                  placeholder="2024"
                  className="h-9"
                  data-testid="cot-anio"
                />
              </FormField>
              <FormField label="Placa">
                <Input
                  value={form.placa}
                  onChange={(e) => setField("placa", e.target.value.toUpperCase())}
                  placeholder="A123456"
                  className="h-9 uppercase"
                  data-testid="cot-placa"
                />
              </FormField>
              <FormField label="Chasis">
                <Input
                  value={form.chasis}
                  onChange={(e) => setField("chasis", e.target.value.toUpperCase())}
                  placeholder="VIN / Chasis"
                  className="h-9 uppercase"
                  data-testid="cot-chasis"
                />
              </FormField>
              <FormField label="Color">
                <Input
                  value={form.color}
                  onChange={(e) => setField("color", e.target.value)}
                  placeholder="Blanco, Negro, Gris…"
                  className="h-9"
                  data-testid="cot-color"
                />
              </FormField>
              <FormField label="Valor asegurado (DOP)" required className="md:col-span-2">
                <Input
                  type="number" min={0} step={1000}
                  value={form.valor_asegurado}
                  onChange={(e) => setField("valor_asegurado", e.target.value)}
                  placeholder="1500000"
                  className="h-9"
                  data-testid="cot-valor"
                />
              </FormField>
            </div>
          </div>

          {/* Sección 3: Aseguradoras */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Aseguradoras a comparar
                <Badge variant="outline" className="ml-2 text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700">
                  {totalPlanesSeleccionados} plan{totalPlanesSeleccionados !== 1 ? "es" : ""} seleccionado{totalPlanesSeleccionados !== 1 ? "s" : ""}
                </Badge>
              </p>
            </div>
            {aseguradorasPrincipales.length === 0 ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                No hay aseguradoras principales configuradas. Pídale al admin que ejecute "Cargar catálogo estándar" en Configuración.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {aseguradorasPrincipales.map((aseg) => {
                  const allChecked = planesAseguradoraTodos(aseg);
                  const anyChecked = planesAseguradoraAlguno(aseg);
                  return (
                    <div
                      key={aseg.id}
                      className={`border rounded-lg overflow-hidden transition-all ${anyChecked ? "border-[#80BC00] shadow-md bg-emerald-50/30" : "border-slate-200 bg-white"}`}
                      data-testid={`cot-aseg-card-${aseg.id}`}
                    >
                      <label className="flex items-center gap-2 p-3 cursor-pointer border-b border-slate-100">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={(e) => toggleAseguradoraCompleta(aseg, e.target.checked)}
                          className="w-4 h-4"
                          data-testid={`cot-aseg-toggle-${aseg.id}`}
                        />
                        <AseguradoraLogo logoUrl={aseg.logo_url} nombre={aseg.nombre} />
                        <span className="font-bold text-xs text-slate-800 flex-1">{aseg.nombre}</span>
                      </label>
                      <div className="p-2 space-y-1 bg-white">
                        {(aseg.planes || []).map((p) => (
                          <label
                            key={p.id}
                            className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-xs"
                          >
                            <input
                              type="checkbox"
                              checked={planesSel[p.id] || false}
                              onChange={(e) => togglePlan(p.id, e.target.checked)}
                              data-testid={`cot-plan-${p.id}`}
                            />
                            <FileText className="w-3 h-3 text-[#80BC00]" />
                            <span className="flex-1 truncate" title={p.descripcion}>{p.nombre}</span>
                          </label>
                        ))}
                        {(aseg.planes || []).length === 0 && (
                          <p className="text-[10px] text-slate-400 px-2 py-1 italic">Sin planes configurados</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Acciones */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setForm({
                  fecha_cotizacion: todayIso,
                  empleado_nombre: "", cedula: "", tipo_vehiculo: "", marca: "", modelo: "",
                  anio: "", placa: "", chasis: "", color: "", valor_asegurado: "",
                });
                setPlanesSel({});
              }}
              data-testid="cot-reset"
            >
              Limpiar
            </Button>
            <Button
              onClick={handleGenerar}
              disabled={creating}
              className="bg-[#80BC00] hover:bg-[#6ea700] text-white"
              data-testid="cot-submit"
            >
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Generar cotización y PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* === HISTÓRICO DE COTIZACIONES RECIENTES === */}
      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4 border-b bg-slate-50">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-[#193966]" /> Cotizaciones recientes
            </CardTitle>
            <Button size="sm" variant="outline" onClick={fetchCotizaciones} className="h-7 text-xs" data-testid="cot-recientes-refresh">
              {loadingCot ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loadingCot ? (
            <div className="p-8 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" /></div>
          ) : cotizaciones.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-xs">
              Aún no hay cotizaciones generadas. Complete el formulario arriba para generar la primera.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#193966] text-white text-xs">
                <tr>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-left">Vehículo</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-center">Estado</th>
                  <th className="px-3 py-2 text-center">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cotizaciones.map((c) => {
                  const v = c.vehiculo_snapshot || {};
                  return (
                    <tr key={c.id} className="hover:bg-slate-50" data-testid={`cot-row-${c.id}`}>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {v.fecha_cotizacion || new Date(c.created_at).toLocaleDateString("es-DO")}
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-800">{v.empleado_nombre} <span className="text-slate-400 text-xs">· {v.cedula}</span></td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        {v.tipo_vehiculo ? <span className="text-slate-400">{v.tipo_vehiculo} · </span> : null}
                        {v.marca} {v.modelo} <span className="text-slate-400">{v.anio || ""}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">DOP {Number(v.valor_asegurado || 0).toLocaleString("es-DO")}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant="outline" className="text-[10px]">{c.estado}</Badge>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setVerCotId(c.id)}
                          className="h-7 text-xs"
                          data-testid={`cot-ver-${c.id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" /> Ver / PDF
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {verCotId && (
        <CotizacionDetalle cotizacionId={verCotId} onClose={() => setVerCotId(null)} />
      )}
    </div>
  );
}

function FormField({ label, required, className = "", children }) {
  return (
    <div className={className}>
      <Label className="text-[11px] text-slate-600 font-medium">
        {label} {required && <span className="text-rose-500">*</span>}
      </Label>
      {children}
    </div>
  );
}

function AseguradoraLogo({ logoUrl, nombre }) {
  const [err, setErr] = useState(false);
  if (logoUrl && !err) {
    return (
      <img
        src={logoUrl}
        alt={nombre}
        onError={() => setErr(true)}
        className="w-8 h-8 rounded object-contain bg-white border border-slate-100"
      />
    );
  }
  // Fallback: badge con iniciales
  const initials = (nombre || "?")
    .split(/\s+/)
    .filter((w) => /^[A-Za-zÁÉÍÓÚÑ]/.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  return (
    <div className="w-8 h-8 rounded bg-gradient-to-br from-[#193966] to-[#80BC00] text-white font-bold text-[10px] flex items-center justify-center">
      {initials || "AS"}
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <Card className="border-0 shadow-md overflow-hidden">
      <div className={`${color} text-white p-4`}>
        <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </div>
    </Card>
  );
}

function VehiculoForm({ open, onOpenChange, editing, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(editing || { empleado_nombre: "", cedula: "", email: "", marca: "", modelo: "", anio: new Date().getFullYear(), chasis: "", placa: "", valor_asegurado: 0, tipo: "Empleado", activo: true });
  }, [editing, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editing ? "PUT" : "POST";
      const url = editing ? `${API_URL}/api/pcc/padron/${editing.id}` : `${API_URL}/api/pcc/padron`;
      const r = await fetch(url, { method, headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }, body: JSON.stringify({ ...form, anio: Number(form.anio), valor_asegurado: Number(form.valor_asegurado) }) });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || "fail"); }
      toast.success(editing ? "Vehículo actualizado" : "Vehículo agregado");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(err.message || "Error al guardar"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="padron-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar vehículo" : "Nuevo vehículo"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3 text-sm">
          <Field2 label="Empleado / Titular *" value={form.empleado_nombre || ""} onChange={(v) => setForm({...form, empleado_nombre: v})} testid="form-empleado" />
          <Field2 label="Cédula / RNC *" value={form.cedula || ""} onChange={(v) => setForm({...form, cedula: v})} testid="form-cedula" />
          <Field2 label="Email" value={form.email || ""} onChange={(v) => setForm({...form, email: v})} testid="form-email" type="email" />
          <div>
            <Label className="text-xs">Tipo</Label>
            <select value={form.tipo || "Empleado"} onChange={(e) => setForm({...form, tipo: e.target.value})} className="w-full h-9 text-sm border border-slate-300 rounded px-2 bg-white" data-testid="form-tipo">
              {TIPO_OPTIONS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <Field2 label="Marca" value={form.marca || ""} onChange={(v) => setForm({...form, marca: v})} testid="form-marca" />
          <Field2 label="Modelo" value={form.modelo || ""} onChange={(v) => setForm({...form, modelo: v})} testid="form-modelo" />
          <Field2 label="Año" value={form.anio || ""} onChange={(v) => setForm({...form, anio: v})} testid="form-anio" type="number" />
          <Field2 label="Placa" value={form.placa || ""} onChange={(v) => setForm({...form, placa: v})} testid="form-placa" />
          <Field2 label="Chasis / VIN" value={form.chasis || ""} onChange={(v) => setForm({...form, chasis: v})} testid="form-chasis" />
          <Field2 label="Valor asegurado (DOP)" value={form.valor_asegurado || ""} onChange={(v) => setForm({...form, valor_asegurado: v})} testid="form-valor" type="number" />
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700]" data-testid="form-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field2({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm" data-testid={testid} />
    </div>
  );
}

function ImportExcelDialog({ open, onOpenChange, onDone }) {
  const { getAuthHeaders } = useAuth();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API_URL}/api/pcc/padron/import-excel`, { method: "POST", headers: getAuthHeaders().headers, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "fail");
      setResult(d);
      toast.success(`${d.inserted} agregados, ${d.updated} actualizados`);
      onDone();
    } catch (err) { toast.error(err.message || "Error al importar"); }
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setFile(null); setResult(null); } }}>
      <DialogContent className="max-w-lg" data-testid="padron-import-dialog">
        <DialogHeader>
          <DialogTitle>Importar padrón desde Excel</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="font-semibold text-blue-900">Columnas esperadas:</p>
            <p className="mt-1">Empleado · Cédula · Email · Marca · Modelo · Año · Chasis · Placa · Valor · Tipo</p>
            <p className="text-[11px] mt-2 italic">El sistema detecta automáticamente las columnas y hace upsert por cédula + chasis.</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-emerald-900">¿No tenés un archivo aún?</p>
              <p className="text-[11px] text-emerald-700">Descargá la plantilla oficial con ejemplos e instrucciones.</p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const r = await fetch(`${API_URL}/api/pcc/padron/plantilla.xlsx`, { headers: getAuthHeaders().headers });
                  if (!r.ok) throw new Error("No se pudo descargar");
                  const blob = await r.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "plantilla_padron_alnap.xlsx";
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Plantilla descargada");
                } catch (err) { toast.error(err.message || "Error al descargar"); }
              }}
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-100 h-8 text-xs"
              data-testid="padron-download-template-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Descargar plantilla
            </Button>
          </div>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0])}
            className="text-sm"
            data-testid="import-file-input"
          />
          {result && (
            <div className="text-sm bg-emerald-50 border border-emerald-200 rounded p-3">
              <p className="font-bold text-emerald-700">✓ Importación completada</p>
              <p className="text-xs mt-1">Agregados: <b>{result.inserted}</b> · Actualizados: <b>{result.updated}</b> · Errores: <b>{result.total_errores}</b></p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          <Button onClick={handleUpload} disabled={!file || uploading} className="bg-[#80BC00] hover:bg-[#6ea700]" data-testid="import-upload-btn">
            {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Subir archivo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// CONFIGURACIÓN
// ============================================================================
function ConfiguracionSection() {
  const [tab, setTab] = useState("aseguradoras");
  return (
    <div className="space-y-4">
      <Card className="border-0 bg-gradient-to-br from-[#193966] to-[#0F2240] text-white">
        <CardContent className="p-4 flex items-center gap-3">
          <Settings className="w-8 h-8 text-emerald-300" />
          <div>
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold">CONFIGURACIÓN QASESORES</p>
            <p className="text-sm text-blue-100/90">Aseguradoras, coberturas y tasas vigentes. Solo administradores.</p>
          </div>
        </CardContent>
      </Card>
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {[
          { id: "aseguradoras", label: "Aseguradoras", icon: ShieldCheck },
          { id: "coberturas", label: "Planes y Coberturas", icon: ShieldAlert },
          { id: "tasas", label: "Tasas Vigentes", icon: Percent },
        ].map((t) => {
          const Ic = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`conf-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                tab === t.id ? "border-[#80BC00] text-[#193966]" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}><Ic className="w-3.5 h-3.5" /> {t.label}</button>
          );
        })}
      </div>
      {tab === "aseguradoras" && <AseguradorasCRUD />}
      {tab === "coberturas" && <PlanesCoberturasCRUD />}
      {tab === "tasas" && <TasasCRUD />}
    </div>
  );
}

function AseguradorasCRUD() {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const fetchData = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pcc/aseguradoras`, getAuthHeaders());
    const d = await r.json();
    setItems(d.items || []);
  }, [getAuthHeaders]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSeed = async () => {
    const overwrite = window.confirm(
      "Vas a cargar el catálogo estándar (5 aseguradoras × 2 planes = 10 entradas, ~75 coberturas y 10 tasas orientativas).\n\n" +
      "¿Sobrescribir si ya existen?\n" +
      "  • Aceptar: reemplaza coberturas/tasas previas en las que coincidan.\n" +
      "  • Cancelar: solo agrega las nuevas."
    );
    setSeeding(true);
    try {
      const r = await fetch(`${API_URL}/api/pcc/seed-defaults?overwrite=${overwrite}`, { method: "POST", headers: getAuthHeaders().headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      toast.success(d.mensaje || "Catálogo cargado");
      fetchData();
    } catch (err) { toast.error(err.message); }
    setSeeding(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta aseguradora?")) return;
    try {
      const r = await fetch(`${API_URL}/api/pcc/aseguradoras/${id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success("Aseguradora eliminada");
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="py-3 px-4 border-b bg-slate-50 flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm font-bold">Aseguradoras configuradas</CardTitle>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={handleSeed}
            disabled={seeding}
            className="text-xs h-8 border-amber-400 text-amber-700 hover:bg-amber-50"
            data-testid="aseg-seed-btn"
            title="Carga 5 aseguradoras DR × 2 planes (Daños a Terceros / Full) con coberturas y tasas orientativas"
          >
            {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Cargar catálogo estándar
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-[#80BC00] hover:bg-[#6ea700] text-xs h-8" data-testid="aseg-add">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Agregar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-[#193966] text-white text-xs">
            <tr><th className="px-3 py-2 text-left">Aseguradora</th><th className="px-3 py-2 text-left">Contacto</th><th className="px-3 py-2 text-left">Vigencia</th><th className="px-3 py-2 text-center">Estado</th><th className="px-3 py-2 text-center">Acción</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Sin aseguradoras. Agregá la primera.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50" data-testid={`aseg-row-${a.id}`}>
                <td className="px-3 py-2.5 font-bold text-slate-800">{a.nombre}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600"><p>{a.contacto_nombre}</p><p className="text-slate-400">{a.contacto_email}</p><p className="text-slate-400">{a.contacto_tel}</p></td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{a.vigencia_desde} → {a.vigencia_hasta}</td>
                <td className="px-3 py-2.5 text-center">
                  <Badge variant="outline" className={a.activa ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-slate-200 text-slate-600"}>
                    {a.activa ? "Activa" : "Pausada"}
                  </Badge>
                </td>
                <td className="px-3 py-2.5 text-center">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(a); setShowForm(true); }} className="h-7 w-7 p-0 mr-1"><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(a.id)} className="h-7 w-7 p-0 text-rose-600 border-rose-200"><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      <AseguradoraForm open={showForm} onOpenChange={setShowForm} editing={editing} onSaved={fetchData} />
    </Card>
  );
}

function AseguradoraForm({ open, onOpenChange, editing, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(editing || { nombre: "", contacto_nombre: "", contacto_email: "", contacto_tel: "", vigencia_desde: "", vigencia_hasta: "", activa: true });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editing ? "PUT" : "POST";
      const url = editing ? `${API_URL}/api/pcc/aseguradoras/${editing.id}` : `${API_URL}/api/pcc/aseguradoras`;
      const r = await fetch(url, { method, headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!r.ok) throw new Error();
      toast.success("Guardado");
      onOpenChange(false); onSaved();
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Editar aseguradora" : "Nueva aseguradora"}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <Field2 label="Nombre *" value={form.nombre || ""} onChange={(v) => setForm({...form, nombre: v})} testid="aseg-form-nombre" />
          <Field2 label="Contacto comercial" value={form.contacto_nombre || ""} onChange={(v) => setForm({...form, contacto_nombre: v})} />
          <Field2 label="Email contacto" value={form.contacto_email || ""} onChange={(v) => setForm({...form, contacto_email: v})} type="email" />
          <Field2 label="Teléfono contacto" value={form.contacto_tel || ""} onChange={(v) => setForm({...form, contacto_tel: v})} />
          <div className="grid grid-cols-2 gap-3">
            <Field2 label="Vigencia desde" value={form.vigencia_desde || ""} onChange={(v) => setForm({...form, vigencia_desde: v})} type="date" />
            <Field2 label="Vigencia hasta" value={form.vigencia_hasta || ""} onChange={(v) => setForm({...form, vigencia_hasta: v})} type="date" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.activa || false} onChange={(e) => setForm({...form, activa: e.target.checked})} data-testid="aseg-form-activa" />
            Activa para cotizaciones
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700]" data-testid="aseg-form-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// PLANES Y COBERTURAS — gestión jerárquica: Aseguradora → Plan → Coberturas
// ============================================================================
function PlanesCoberturasCRUD() {
  const { getAuthHeaders } = useAuth();
  const [aseguradoras, setAseguradoras] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [coberturas, setCoberturas] = useState({}); // map plan_id -> [coberturas]
  const [expanded, setExpanded] = useState({}); // map plan_id -> bool
  const [loading, setLoading] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [defaultAseguradoraForPlan, setDefaultAseguradoraForPlan] = useState("");
  const [showCobForm, setShowCobForm] = useState(false);
  const [editingCob, setEditingCob] = useState(null);
  const [defaultPlanForCob, setDefaultPlanForCob] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ra, rp] = await Promise.all([
        fetch(`${API_URL}/api/pcc/aseguradoras`, getAuthHeaders()).then(r => r.json()),
        fetch(`${API_URL}/api/pcc/planes`, getAuthHeaders()).then(r => r.json()),
      ]);
      setAseguradoras(ra.items || []);
      setPlanes(rp.items || []);
    } catch { toast.error("Error al cargar planes"); }
    setLoading(false);
  }, [getAuthHeaders]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const togglePlan = async (planId) => {
    const nowExpanded = !expanded[planId];
    setExpanded(e => ({ ...e, [planId]: nowExpanded }));
    if (nowExpanded && !coberturas[planId]) {
      try {
        const r = await fetch(`${API_URL}/api/pcc/coberturas?plan_id=${planId}`, getAuthHeaders());
        const d = await r.json();
        setCoberturas(c => ({ ...c, [planId]: d.items || [] }));
      } catch { toast.error("Error al cargar coberturas"); }
    }
  };

  const refreshCoberturas = async (planId) => {
    try {
      const r = await fetch(`${API_URL}/api/pcc/coberturas?plan_id=${planId}`, getAuthHeaders());
      const d = await r.json();
      setCoberturas(c => ({ ...c, [planId]: d.items || [] }));
    } catch { /* noop */ }
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`¿Eliminar el plan "${plan.nombre}"?\nSolo se permite si no tiene coberturas ni tasas vinculadas.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pcc/planes/${plan.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success("Plan eliminado");
      fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteCob = async (planId, cobId) => {
    if (!window.confirm("¿Eliminar cobertura?")) return;
    const r = await fetch(`${API_URL}/api/pcc/coberturas/${cobId}`, { method: "DELETE", headers: getAuthHeaders().headers });
    if (r.ok) { toast.success("Eliminada"); refreshCoberturas(planId); } else toast.error("Error");
  };

  // Agrupar planes por aseguradora
  const planesPorAseg = aseguradoras.map(a => ({
    aseguradora: a,
    planes: planes.filter(p => p.aseguradora_id === a.id),
  }));

  return (
    <Card className="border-slate-200">
      <CardHeader className="py-3 px-4 border-b bg-gradient-to-r from-blue-50 to-slate-50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm font-bold">Planes y Coberturas</CardTitle>
            <p className="text-[11px] text-slate-500 mt-0.5">Cada plan pertenece a una aseguradora y agrupa sus propias coberturas.</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? <div className="p-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div> : (
          <div className="divide-y divide-slate-200">
            {planesPorAseg.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm">Sin aseguradoras configuradas. Agregá una primero.</div>
            )}
            {planesPorAseg.map(({ aseguradora, planes: aPlanes }) => (
              <div key={aseguradora.id} className="bg-white" data-testid={`aseg-block-${aseguradora.id}`}>
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#193966]" />
                    <span className="font-bold text-sm text-slate-800">{aseguradora.nombre}</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">{aPlanes.length} planes</Badge>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => { setEditingPlan(null); setDefaultAseguradoraForPlan(aseguradora.id); setShowPlanForm(true); }}
                    className="bg-[#80BC00] hover:bg-[#6ea700] text-xs h-7"
                    data-testid={`plan-add-${aseguradora.id}`}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Nuevo plan
                  </Button>
                </div>
                {aPlanes.length === 0 ? (
                  <p className="text-center text-slate-400 text-xs py-4">Esta aseguradora no tiene planes configurados.</p>
                ) : aPlanes.map(p => {
                  const isOpen = expanded[p.id];
                  const cobs = coberturas[p.id] || [];
                  return (
                    <div key={p.id} className="border-t border-slate-100" data-testid={`plan-block-${p.id}`}>
                      <button
                        onClick={() => togglePlan(p.id)}
                        className="w-full flex items-center justify-between px-6 py-2.5 hover:bg-blue-50/30 text-left transition-colors"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <span className={`text-slate-400 text-xs transition-transform ${isOpen ? "rotate-90" : ""}`}>▶</span>
                          <FileText className="w-4 h-4 text-[#80BC00]" />
                          <div>
                            <p className="font-semibold text-sm text-slate-800">{p.nombre}</p>
                            {p.descripcion && <p className="text-[11px] text-slate-500">{p.descripcion}</p>}
                          </div>
                          <Badge variant="outline" className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">{p.coberturas_count} cob.</Badge>
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{p.tasas_count} tasas</Badge>
                          {!p.activo && <Badge className="bg-slate-300 text-slate-700 text-[10px]">Inactivo</Badge>}
                        </div>
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" onClick={() => { setEditingPlan(p); setShowPlanForm(true); }} className="h-6 w-6 p-0" data-testid={`plan-edit-${p.id}`}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeletePlan(p)} className="h-6 w-6 p-0 text-rose-600 border-rose-200" data-testid={`plan-delete-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="bg-slate-50/50 px-8 py-3">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">Coberturas del plan</p>
                            <Button size="sm" variant="outline" onClick={() => { setEditingCob(null); setDefaultPlanForCob(p.id); setShowCobForm(true); }} className="h-7 text-xs" data-testid={`cob-add-${p.id}`}>
                              <Plus className="w-3 h-3 mr-1" /> Agregar cobertura
                            </Button>
                          </div>
                          {cobs.length === 0 ? (
                            <p className="text-center text-slate-400 text-xs py-3">Este plan aún no tiene coberturas.</p>
                          ) : (
                            <table className="w-full text-xs bg-white rounded border border-slate-200">
                              <thead className="bg-slate-100 text-slate-700">
                                <tr>
                                  <th className="px-2 py-1.5 text-left">Cobertura</th>
                                  <th className="px-2 py-1.5 text-left">Límite</th>
                                  <th className="px-2 py-1.5 text-left">Deducible</th>
                                  <th className="px-2 py-1.5 text-center">Obligatoria</th>
                                  <th className="px-2 py-1.5 text-center w-20">Acción</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {cobs.map(c => (
                                  <tr key={c.id} className="hover:bg-blue-50/20" data-testid={`cob-row-${c.id}`}>
                                    <td className="px-2 py-1.5 font-medium">{c.nombre}</td>
                                    <td className="px-2 py-1.5 font-mono text-[10px]">{c.limite || "—"}</td>
                                    <td className="px-2 py-1.5 font-mono text-[10px]">{c.deducible || "—"}</td>
                                    <td className="px-2 py-1.5 text-center">{c.obligatoria ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mx-auto" /> : <span className="text-slate-300">—</span>}</td>
                                    <td className="px-2 py-1.5 text-center">
                                      <Button size="sm" variant="outline" onClick={() => { setEditingCob({ ...c }); setDefaultPlanForCob(p.id); setShowCobForm(true); }} className="h-6 w-6 p-0 mr-1"><Pencil className="w-3 h-3" /></Button>
                                      <Button size="sm" variant="outline" onClick={() => handleDeleteCob(p.id, c.id)} className="h-6 w-6 p-0 text-rose-600 border-rose-200"><Trash2 className="w-3 h-3" /></Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <PlanForm
        open={showPlanForm}
        onOpenChange={setShowPlanForm}
        editing={editingPlan}
        defaultAseguradoraId={defaultAseguradoraForPlan}
        aseguradoras={aseguradoras}
        onSaved={fetchAll}
      />
      <CoberturaForm
        open={showCobForm}
        onOpenChange={setShowCobForm}
        editing={editingCob}
        defaultPlanId={defaultPlanForCob}
        planes={planes}
        onSaved={() => refreshCoberturas(defaultPlanForCob)}
      />
    </Card>
  );
}

function PlanForm({ open, onOpenChange, editing, defaultAseguradoraId, aseguradoras, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(editing || { aseguradora_id: defaultAseguradoraId || (aseguradoras[0]?.id || ""), nombre: "", descripcion: "", activo: true, orden: 0 });
  }, [editing, open, defaultAseguradoraId, aseguradoras]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editing ? "PUT" : "POST";
      const url = editing ? `${API_URL}/api/pcc/planes/${editing.id}` : `${API_URL}/api/pcc/planes`;
      const payload = { ...form, orden: Number(form.orden) || 0 };
      const r = await fetch(url, { method, headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Error al guardar");
      }
      toast.success("Plan guardado");
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="plan-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editar plan" : "Nuevo plan"}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Aseguradora *</Label>
            <select value={form.aseguradora_id || ""} onChange={(e) => setForm({...form, aseguradora_id: e.target.value})} className="w-full h-9 text-sm border border-slate-300 rounded px-2 bg-white" data-testid="plan-form-aseg">
              {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
          <Field2 label="Nombre del plan *" value={form.nombre || ""} onChange={(v) => setForm({...form, nombre: v})} testid="plan-form-nombre" />
          <div>
            <Label className="text-xs">Descripción</Label>
            <textarea value={form.descripcion || ""} onChange={(e) => setForm({...form, descripcion: e.target.value})} className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 min-h-[60px]" placeholder="Ej: Cobertura mínima legal, solo daños a terceros…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field2 label="Orden de visualización" type="number" value={form.orden || 0} onChange={(v) => setForm({...form, orden: v})} />
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-6">
              <input type="checkbox" checked={form.activo ?? true} onChange={(e) => setForm({...form, activo: e.target.checked})} />
              Plan activo
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700]" data-testid="plan-form-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CoberturaForm({ open, onOpenChange, editing, defaultPlanId, planes, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(editing || { plan_id: defaultPlanId || (planes[0]?.id || ""), nombre: "", limite: "", deducible: "", obligatoria: false, orden: 0 });
  }, [editing, open, defaultPlanId, planes]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API_URL}/api/pcc/coberturas/${editing.id}` : `${API_URL}/api/pcc/coberturas`;
    const payload = { ...form, plan_id: form.plan_id || defaultPlanId, orden: Number(form.orden) || 0 };
    const r = await fetch(url, { method, headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (r.ok) { toast.success("Cobertura guardada"); onOpenChange(false); onSaved(); }
    else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || "Error al guardar");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cob-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editar cobertura" : "Nueva cobertura"}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs">Plan al que pertenece *</Label>
            <select value={form.plan_id || ""} onChange={(e) => setForm({...form, plan_id: e.target.value})} className="w-full h-9 text-sm border border-slate-300 rounded px-2 bg-white" data-testid="cob-form-plan">
              {planes.map(p => <option key={p.id} value={p.id}>{p.aseguradora_nombre} — {p.nombre}</option>)}
            </select>
          </div>
          <Field2 label="Nombre de la cobertura *" value={form.nombre || ""} onChange={(v) => setForm({...form, nombre: v})} testid="cob-form-nombre" />
          <Field2 label="Límite / Suma asegurada" value={form.limite || ""} onChange={(v) => setForm({...form, limite: v})} />
          <Field2 label="Deducible" value={form.deducible || ""} onChange={(v) => setForm({...form, deducible: v})} />
          <Field2 label="Orden de visualización" type="number" value={form.orden || 0} onChange={(v) => setForm({...form, orden: Number(v) || 0})} />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.obligatoria || false} onChange={(e) => setForm({...form, obligatoria: e.target.checked})} />
            Cobertura obligatoria
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#80BC00] hover:bg-[#6ea700]" data-testid="cob-form-save">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TasasCRUD() {
  const { getAuthHeaders } = useAuth();
  const [aseguradoras, setAseguradoras] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showHistorial, setShowHistorial] = useState(null);
  const [aseguradoraFilter, setAseguradoraFilter] = useState("");

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/pcc/aseguradoras`, getAuthHeaders()).then(r => r.json()),
      fetch(`${API_URL}/api/pcc/planes`, getAuthHeaders()).then(r => r.json()),
    ]).then(([a, p]) => {
      setAseguradoras(a.items || []);
      setPlanes(p.items || []);
    });
  }, [getAuthHeaders]);

  const fetchTasas = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pcc/tasas?vigentes=false`, getAuthHeaders());
    const d = await r.json();
    setItems(d.items || []);
  }, [getAuthHeaders]);
  useEffect(() => { fetchTasas(); }, [fetchTasas]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar tasa? Quedará registrada en histórico. Las cotizaciones ya generadas con esta tasa conservan su valor (snapshot).")) return;
    const r = await fetch(`${API_URL}/api/pcc/tasas/${id}`, { method: "DELETE", headers: getAuthHeaders().headers });
    if (r.ok) { toast.success("Eliminada"); fetchTasas(); } else toast.error("Error");
  };

  // Agrupar tasas por aseguradora → plan
  const grouped = aseguradoras
    .filter(a => !aseguradoraFilter || a.id === aseguradoraFilter)
    .map(a => {
      const aPlanes = planes.filter(p => p.aseguradora_id === a.id);
      return {
        aseguradora: a,
        planes: aPlanes.map(p => ({
          plan: p,
          tasas: items
            .filter(t => t.plan_id === p.id)
            .sort((a, b) => (b.anio_desde || 0) - (a.anio_desde || 0)),
        })),
      };
    });

  return (
    <Card className="border-slate-200">
      <CardHeader className="py-3 px-4 border-b bg-gradient-to-r from-emerald-50 to-slate-50 flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-sm font-bold">Tasas vigentes para cotización</CardTitle>
          <p className="text-[11px] text-slate-500 mt-0.5">Cada tasa pertenece a un plan. Las cotizaciones ya firmadas conservan la tasa que tenían al momento de generarse.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={aseguradoraFilter} onChange={(e) => setAseguradoraFilter(e.target.value)} className="h-8 text-xs border border-slate-300 rounded px-2 bg-white" data-testid="tasa-filter-aseg">
            <option value="">Todas las aseguradoras</option>
            {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} disabled={planes.length === 0} className="bg-[#80BC00] hover:bg-[#6ea700] text-xs h-8" data-testid="tasa-add">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Nueva tasa
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-slate-200">
          {grouped.length === 0 && (
            <p className="p-6 text-center text-slate-400 text-sm">Sin aseguradoras configuradas.</p>
          )}
          {grouped.map(({ aseguradora, planes: aPlanes }) => (
            <div key={aseguradora.id} className="bg-white">
              <div className="px-4 py-2.5 bg-slate-50 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#193966]" />
                <span className="font-bold text-sm">{aseguradora.nombre}</span>
                <Badge variant="outline" className="text-[10px]">{aPlanes.reduce((s, p) => s + p.tasas.length, 0)} tasas</Badge>
              </div>
              {aPlanes.length === 0 && <p className="px-6 py-3 text-xs text-slate-400">Sin planes configurados.</p>}
              {aPlanes.map(({ plan, tasas }) => (
                <div key={plan.id} className="px-6 py-2 border-t border-slate-100">
                  <div className="flex items-center gap-2 mb-1.5">
                    <FileText className="w-3.5 h-3.5 text-[#80BC00]" />
                    <span className="text-sm font-semibold text-slate-700">{plan.nombre}</span>
                    {tasas.length === 0 && <span className="text-[10px] text-rose-500 italic">Sin tasa configurada</span>}
                  </div>
                  {tasas.length > 0 && (
                    <table className="w-full text-xs bg-white rounded border border-slate-200">
                      <thead className="bg-slate-100 text-slate-700">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Segmento</th>
                          <th className="px-2 py-1.5 text-left">Año</th>
                          <th className="px-2 py-1.5 text-right">Tasa %</th>
                          <th className="px-2 py-1.5 text-right">Deducible</th>
                          <th className="px-2 py-1.5 text-left">Vigencia</th>
                          <th className="px-2 py-1.5 text-center w-28">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {tasas.map(t => (
                          <tr key={t.id} className="hover:bg-emerald-50/30" data-testid={`tasa-row-${t.id}`}>
                            <td className="px-2 py-1.5">{t.segmento}</td>
                            <td className="px-2 py-1.5">{t.anio_desde || "Todos"}</td>
                            <td className="px-2 py-1.5 text-right"><Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 font-mono text-[10px]">{t.tasa_porcentaje}%</Badge></td>
                            <td className="px-2 py-1.5 text-right font-mono">DOP {Number(t.deducible_base || 0).toLocaleString("es-DO")}</td>
                            <td className="px-2 py-1.5 text-emerald-700 text-[10px]">{t.vigencia_desde} → {t.vigencia_hasta || "Sin vto."}</td>
                            <td className="px-2 py-1.5 text-center">
                              <Button size="sm" variant="outline" onClick={() => setShowHistorial(t.id)} className="h-6 w-6 p-0 mr-0.5" title="Histórico" data-testid={`tasa-hist-${t.id}`}><Clock className="w-3 h-3" /></Button>
                              <Button size="sm" variant="outline" onClick={() => { setEditing(t); setShowForm(true); }} className="h-6 w-6 p-0 mr-0.5" data-testid={`tasa-edit-${t.id}`}><Pencil className="w-3 h-3" /></Button>
                              <Button size="sm" variant="outline" onClick={() => handleDelete(t.id)} className="h-6 w-6 p-0 text-rose-600 border-rose-200" data-testid={`tasa-delete-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 flex items-center gap-2 text-xs text-blue-900">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Cualquier cambio de tasa queda registrado en el histórico con fecha, hora y usuario. Las cotizaciones ya generadas mantienen la tasa con la que fueron creadas (no se reescriben).</span>
        </div>
      </CardContent>
      <TasaForm open={showForm} onOpenChange={setShowForm} editing={editing} planes={planes} onSaved={fetchTasas} />
      <HistorialDialog tasaId={showHistorial} onClose={() => setShowHistorial(null)} />
    </Card>
  );
}

function TasaForm({ open, onOpenChange, editing, planes, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(editing || { plan_id: planes[0]?.id || "", segmento: "General", anio: "", tasa_porcentaje: 0, deducible_base: 0, vigencia_desde: "", vigencia_hasta: "", notas: "" });
  }, [editing, open, planes]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API_URL}/api/pcc/tasas/${editing.id}` : `${API_URL}/api/pcc/tasas`;
    // Campo único "Año" → se almacena como anio_desde = anio_hasta = año
    const anioRaw = form.anio ?? form.anio_desde;  // retrocompat al editar
    if (!anioRaw) {
      toast.error("El año del vehículo es obligatorio");
      return;
    }
    const anioNum = Number(anioRaw);
    const payload = {
      plan_id: form.plan_id,
      segmento: form.segmento || "General",
      anio_desde: anioNum,
      anio_hasta: anioNum,
      tasa_porcentaje: Number(form.tasa_porcentaje),
      deducible_base: Number(form.deducible_base),
      vigencia_desde: form.vigencia_desde || "",
      vigencia_hasta: form.vigencia_hasta || "",
      notas: form.notas || "",
    };
    const r = await fetch(url, { method, headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (r.ok) { toast.success("Tasa guardada"); onOpenChange(false); onSaved(); }
    else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || "Error al guardar");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Editar tasa" : "Nueva tasa"}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Plan al que aplica esta tasa *</Label>
            <select value={form.plan_id || ""} onChange={(e) => setForm({...form, plan_id: e.target.value})} className="w-full h-9 text-sm border border-slate-300 rounded px-2 bg-white" data-testid="tasa-form-plan">
              {planes.length === 0 && <option value="">— Sin planes —</option>}
              {planes.map(p => <option key={p.id} value={p.id}>{p.aseguradora_nombre} — {p.nombre}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Segmento (opcional)</Label>
            <select value={form.segmento || "General"} onChange={(e) => setForm({...form, segmento: e.target.value})} className="w-full h-9 text-sm border border-slate-300 rounded px-2 bg-white">
              <option value="General">General (todos los empleados)</option>
              {SEGMENTO_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field2
              label="Año del vehículo *"
              type="number"
              value={form.anio ?? form.anio_desde ?? ""}
              onChange={(v) => setForm({...form, anio: v})}
              testid="tasa-form-anio"
            />
            <Field2 label="Tasa % *" type="number" value={form.tasa_porcentaje || 0} onChange={(v) => setForm({...form, tasa_porcentaje: v})} testid="tasa-form-tasa" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field2 label="Deducible base" type="number" value={form.deducible_base || 0} onChange={(v) => setForm({...form, deducible_base: v})} />
            <Field2 label="Vigencia desde" type="date" value={form.vigencia_desde || ""} onChange={(v) => setForm({...form, vigencia_desde: v})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field2 label="Vigencia hasta" type="date" value={form.vigencia_hasta || ""} onChange={(v) => setForm({...form, vigencia_hasta: v})} />
          </div>
          <div>
            <Label className="text-xs">Notas internas</Label>
            <Input value={form.notas || ""} onChange={(e) => setForm({...form, notas: e.target.value})} className="h-9 text-sm" placeholder="Ej: tasa promocional Q2 2026" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#80BC00] hover:bg-[#6ea700]" data-testid="tasa-form-save">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HistorialDialog({ tasaId, onClose }) {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tasaId) { setItems([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/pcc/tasas/${tasaId}/historico`, getAuthHeaders());
        if (cancelled) return;
        if (!r.ok) {
          const txt = await r.text().catch(() => "");
          throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`);
        }
        const d = await r.json();
        if (cancelled) return;
        setItems(d.items || []);
      } catch (err) {
        if (!cancelled) toast.error(`Error histórico: ${err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tasaId, getAuthHeaders]);

  return (
    <Dialog open={Boolean(tasaId)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="historial-dialog">
        <DialogHeader><DialogTitle>Histórico de cambios de tasa</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[450px] overflow-y-auto">
          {loading && <p className="text-center text-slate-400 py-4 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Cargando…</p>}
          {!loading && items.length === 0 && <p className="text-center text-slate-400 py-6 text-sm">Sin cambios registrados.</p>}
          {items.map((h) => (
            <div key={h.id} className="border border-slate-200 rounded p-3 text-xs" data-testid={`hist-item-${h.id}`}>
              <div className="flex justify-between mb-1">
                <Badge variant="outline" className={h.accion === "eliminada" ? "bg-rose-100 text-rose-700" : h.accion === "creada" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{h.accion}</Badge>
                <span className="text-slate-500">{new Date(h.at).toLocaleString("es-DO")}</span>
              </div>
              <p className="text-slate-600">Por: <b>{h.by || "—"}</b></p>
              {h.snapshot && (
                <p className="text-slate-600 mt-1">Tasa registrada: <b>{h.snapshot.tasa_porcentaje}%</b> · Deducible: <b>{Number(h.snapshot.deducible_base || 0).toLocaleString("es-DO")}</b> · Segmento: {h.snapshot.segmento}</p>
              )}
              {h.snapshot_nuevo && (
                <p className="text-emerald-700 mt-1">Nueva: <b>{h.snapshot_nuevo.tasa_porcentaje}%</b> · Deducible: <b>{Number(h.snapshot_nuevo.deducible_base || 0).toLocaleString("es-DO")}</b></p>
              )}
              {h.snapshot_anterior && (
                <p className="text-rose-700">Anterior: <b>{h.snapshot_anterior.tasa_porcentaje}%</b> · Deducible: <b>{Number(h.snapshot_anterior.deducible_base || 0).toLocaleString("es-DO")}</b></p>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CotizacionesSection({ isAdmin }) {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({});
  const [total, setTotal] = useState(0);
  const [estado, setEstado] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (estado) params.append("estado", estado);
      const r = await fetch(`${API_URL}/api/pcc/cotizaciones?${params}`, getAuthHeaders());
      const d = await r.json();
      setItems(d.items || []);
      setTotal(d.total || 0);
      setKpis(d.kpis || {});
      setSelected(new Set());
    } catch { toast.error("Error al cargar cotizaciones"); }
    setLoading(false);
  }, [getAuthHeaders, page, pageSize, estado]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Eliminar ${ids.length} cotización(es)? Esta acción es masiva.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pcc/cotizaciones/bulk-delete`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      toast.success(`${d.eliminados} cotización(es) eliminada(s)`);
      fetchData();
    } catch { toast.error("No se pudo eliminar masivamente"); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === items.length && items.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(c => c.id)));
    }
  };
  const allSelected = items.length > 0 && selected.size === items.length;

  const estadoBadge = (e) => {
    const colors = {
      generada: "bg-slate-100 text-slate-700 border-slate-300",
      enviada: "bg-blue-100 text-blue-700 border-blue-300",
      vista: "bg-amber-100 text-amber-700 border-amber-300",
      seleccionada: "bg-purple-100 text-purple-700 border-purple-300",
      firmada: "bg-emerald-100 text-emerald-700 border-emerald-300",
      vencida: "bg-rose-100 text-rose-700 border-rose-300",
    };
    return <Badge variant="outline" className={`text-[10px] ${colors[e] || "bg-slate-100"}`}>{e}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total cotizaciones" value={kpis.total || 0} color="bg-[#193966]" />
        <Kpi label="Firmadas" value={kpis.firmadas || 0} color="bg-emerald-600" />
        <Kpi label="Pendientes" value={kpis.pendientes || 0} color="bg-amber-600" />
        <Kpi label="Vencidas" value={kpis.vencidas || 0} color="bg-rose-600" />
      </div>
      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4 border-b bg-slate-50 flex flex-row items-center justify-between flex-wrap gap-2">
          <div className="flex gap-2 items-center">
            <select value={estado} onChange={(e) => { setEstado(e.target.value); setPage(1); }} className="h-8 text-xs border border-slate-300 rounded px-2 bg-white" data-testid="cot-filter-estado">
              <option value="">Todos los estados</option>
              <option value="generada">Generada</option>
              <option value="enviada">Enviada</option>
              <option value="vista">Vista</option>
              <option value="seleccionada">Seleccionada</option>
              <option value="firmada">Firmada</option>
              <option value="vencida">Vencida</option>
            </select>
          </div>
          <Button size="sm" onClick={() => setShowNew(true)} className="bg-[#80BC00] hover:bg-[#6ea700] text-xs h-8" data-testid="cot-new-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Nueva cotización
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {/* Banner de selección múltiple */}
          {isAdmin && selected.size > 0 && (
            <div className="flex items-center justify-between px-4 py-2 bg-rose-50 border-b border-rose-200" data-testid="cot-bulk-bar">
              <span className="text-xs font-semibold text-rose-800">
                {selected.size} cotización(es) seleccionada(s)
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} className="h-7 text-xs">
                  Limpiar selección
                </Button>
                <Button size="sm" onClick={handleBulkDelete} className="h-7 text-xs bg-rose-600 hover:bg-rose-700 text-white" data-testid="cot-bulk-delete">
                  <Trash2 className="w-3 h-3 mr-1" /> Eliminar seleccionadas
                </Button>
              </div>
            </div>
          )}
          {loading ? <div className="p-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div> : (
            <>
            <table className="w-full text-sm">
              <thead className="bg-[#193966] text-white text-xs">
                <tr>
                  {isAdmin && (
                    <th className="px-3 py-2.5 text-center w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        className="cursor-pointer"
                        data-testid="cot-select-all"
                        aria-label="Seleccionar todas"
                      />
                    </th>
                  )}
                  <th className="px-3 py-2.5 text-left">Empleado</th>
                  <th className="px-3 py-2.5 text-left">Vehículo</th>
                  <th className="px-3 py-2.5 text-center">Estado</th>
                  <th className="px-3 py-2.5 text-left">Selección</th>
                  <th className="px-3 py-2.5 text-right">Fecha</th>
                  <th className="px-3 py-2.5 text-center">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="p-8 text-center text-slate-400">Sin cotizaciones. Generá la primera.</td></tr>}
                {items.map((c) => (
                  <tr key={c.id} className={`hover:bg-slate-50 ${selected.has(c.id) ? "bg-rose-50/40" : ""}`} data-testid={`cot-row-${c.id}`}>
                    {isAdmin && (
                      <td className="px-3 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={selected.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          className="cursor-pointer"
                          data-testid={`cot-select-${c.id}`}
                          aria-label={`Seleccionar cotización ${c.id}`}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2.5">
                      <p className="font-bold text-slate-800">{c.vehiculo_snapshot?.empleado_nombre}</p>
                      <p className="text-[10px] text-slate-400">{c.vehiculo_snapshot?.cedula}</p>
                    </td>
                    <td className="px-3 py-2.5 text-xs">{c.vehiculo_snapshot?.marca} {c.vehiculo_snapshot?.modelo} {c.vehiculo_snapshot?.anio}</td>
                    <td className="px-3 py-2.5 text-center">{estadoBadge(c.estado)}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {c.seleccion ? (
                        <><b>{c.seleccion.aseguradora_nombre}</b><br /><span className="text-slate-500">DOP {Number(c.seleccion.prima_anual).toLocaleString("es-DO")}</span></>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString("es-DO")}</td>
                    <td className="px-3 py-2.5 text-center">
                      <Button size="sm" variant="outline" onClick={() => setDetalle(c.id)} className="h-7 text-xs" data-testid={`cot-detalle-${c.id}`}>
                        <Eye2 /> Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Footer: page size selector + paginación */}
            <div className="flex items-center justify-between px-4 py-2 border-t bg-slate-50 text-xs flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Mostrar:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="h-7 text-xs border border-slate-300 rounded px-2 bg-white"
                  data-testid="cot-page-size"
                >
                  {[20, 25, 50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-slate-500">por página · {items.length} de {total}</span>
              </div>
              {total > pageSize && (
                <div className="flex gap-1 items-center">
                  <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">Anterior</Button>
                  <span className="px-2 py-1">Página {page} de {Math.ceil(total / pageSize)}</span>
                  <Button size="sm" variant="outline" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">Siguiente</Button>
                </div>
              )}
            </div>
            </>
          )}
        </CardContent>
      </Card>
      <NuevaCotizacionDialog open={showNew} onOpenChange={setShowNew} onCreated={(id) => { fetchData(); setDetalle(id); }} />
      <CotizacionDetalle cotizacionId={detalle} onClose={() => { setDetalle(null); fetchData(); }} />
    </div>
  );
}

function Eye2() { return <Eye className="w-3 h-3 mr-1 inline" />; }

function NuevaCotizacionDialog({ open, onOpenChange, onCreated }) {
  const { getAuthHeaders } = useAuth();
  const [search, setSearch] = useState("");
  const [vehiculos, setVehiculos] = useState([]);
  const [vehSel, setVehSel] = useState(null);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [planSel, setPlanSel] = useState({});
  const [recomendadoId, setRecomendadoId] = useState(null);
  const [vigenciaDias, setVigenciaDias] = useState(30);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRecomendadoId(null);
    Promise.all([
      fetch(`${API_URL}/api/pcc/aseguradoras`, getAuthHeaders()).then(r => r.json()),
      fetch(`${API_URL}/api/pcc/planes?incluir_inactivos=false`, getAuthHeaders()).then(r => r.json()),
    ]).then(([a, p]) => {
      const activas = (a.items || []).filter(x => x.activa);
      setAseguradoras(activas);
      const activePlanes = p.items || [];
      setPlanes(activePlanes);
      // Por defecto: seleccionar todos los planes
      setPlanSel(Object.fromEntries(activePlanes.map(pp => [pp.id, true])));
    });
  }, [open, getAuthHeaders]);

  useEffect(() => {
    if (!open || !search || search.length < 2) { setVehiculos([]); return; }
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/pcc/padron?search=${encodeURIComponent(search)}&page_size=10`, getAuthHeaders())
        .then(r => r.json()).then(d => setVehiculos(d.items || []));
    }, 250);
    return () => clearTimeout(t);
  }, [search, open, getAuthHeaders]);

  const handleSubmit = async () => {
    if (!vehSel) { toast.error("Seleccione un vehículo"); return; }
    const ids = Object.keys(planSel).filter(k => planSel[k]);
    if (!ids.length) { toast.error("Seleccione al menos un plan"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API_URL}/api/pcc/cotizaciones`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          vehiculo_id: vehSel.id,
          planes_ids: ids,
          recomendado_plan_id: recomendadoId || null,
          vigencia_dias: Number(vigenciaDias),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "fail");
      toast.success("Cotización generada");
      onOpenChange(false);
      setVehSel(null); setSearch("");
      onCreated(d.id);
    } catch (err) { toast.error(err.message); }
    setCreating(false);
  };

  // Agrupar planes por aseguradora para mostrarlos jerárquicamente
  const planesPorAseg = aseguradoras.map(a => ({
    aseguradora: a,
    planes: planes.filter(p => p.aseguradora_id === a.id),
  })).filter(g => g.planes.length > 0);

  const toggleAseguradora = (asegId, checked) => {
    const aPlanes = planes.filter(p => p.aseguradora_id === asegId);
    setPlanSel({...planSel, ...Object.fromEntries(aPlanes.map(p => [p.id, checked]))});
  };

  const totalSeleccionados = Object.values(planSel).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cot-new-dialog">
        <DialogHeader>
          <DialogTitle>Generar nueva cotización</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">1. Buscar vehículo del padrón</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Empleado, cédula, placa, chasis…" className="h-9" data-testid="cot-search-veh" />
            {vehiculos.length > 0 && !vehSel && (
              <div className="border border-slate-200 rounded mt-1 max-h-[200px] overflow-y-auto bg-white shadow-sm">
                {vehiculos.map(v => (
                  <button key={v.id} onClick={() => { setVehSel(v); setSearch(""); setVehiculos([]); }} className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 text-xs" data-testid={`cot-veh-option-${v.id}`}>
                    <p className="font-bold">{v.empleado_nombre} · {v.cedula}</p>
                    <p className="text-slate-500">{v.marca} {v.modelo} {v.anio} · DOP {Number(v.valor_asegurado).toLocaleString("es-DO")}</p>
                  </button>
                ))}
              </div>
            )}
            {vehSel && (
              <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs">
                <div className="flex justify-between">
                  <div>
                    <p className="font-bold text-emerald-800">{vehSel.empleado_nombre} · {vehSel.cedula}</p>
                    <p className="text-slate-600">{vehSel.marca} {vehSel.modelo} {vehSel.anio} · DOP {Number(vehSel.valor_asegurado).toLocaleString("es-DO")} · {vehSel.tipo}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setVehSel(null)}>Cambiar</Button>
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">2. Planes a comparar <span className="text-slate-500 font-normal">({totalSeleccionados} seleccionados)</span></Label>
              <div className="flex gap-2 text-[10px]">
                <button type="button" onClick={() => setPlanSel(Object.fromEntries(planes.map(p => [p.id, true])))} className="text-[#193966] underline">Marcar todos</button>
                <span className="text-slate-400">·</span>
                <button type="button" onClick={() => setPlanSel({})} className="text-[#193966] underline">Ninguno</button>
              </div>
            </div>
            <div className="space-y-2 border border-slate-200 rounded p-2 max-h-[260px] overflow-y-auto">
              {planesPorAseg.length === 0 && <p className="text-xs text-slate-400 text-center p-2">No hay planes activos. Configurá uno en Configuración → Planes y Coberturas.</p>}
              {planesPorAseg.map(({ aseguradora, planes: aPlanes }) => {
                const allChecked = aPlanes.every(p => planSel[p.id]);
                return (
                  <div key={aseguradora.id} className="border border-slate-100 rounded">
                    <label className="flex items-center gap-2 text-xs cursor-pointer bg-slate-50 px-2 py-1.5 rounded-t font-bold">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={(e) => toggleAseguradora(aseguradora.id, e.target.checked)}
                        data-testid={`cot-aseg-toggle-${aseguradora.id}`}
                      />
                      <ShieldCheck className="w-3.5 h-3.5 text-[#193966]" />
                      <span>{aseguradora.nombre}</span>
                      <Badge variant="outline" className="text-[9px] ml-auto">{aPlanes.length} planes</Badge>
                    </label>
                    <div className="px-2 py-1 space-y-0.5">
                      {aPlanes.map(p => {
                        const isReco = recomendadoId === p.id;
                        const hasReco = Boolean(recomendadoId);
                        const checked = planSel[p.id] || false;
                        return (
                          <div key={p.id} className="flex items-center gap-2 hover:bg-blue-50/30 px-2 py-1 rounded ml-4">
                            <input type="checkbox" checked={checked} onChange={(e) => {
                              setPlanSel({...planSel, [p.id]: e.target.checked});
                              // Si destildo el plan recomendado, lo desmarco
                              if (!e.target.checked && isReco) setRecomendadoId(null);
                            }} data-testid={`cot-plan-cb-${p.id}`} />
                            <FileText className="w-3 h-3 text-[#80BC00]" />
                            <span className="text-xs flex-1">{p.nombre}</span>
                            <button
                              type="button"
                              onClick={() => {
                                if (isReco) {
                                  setRecomendadoId(null);
                                } else if (!checked) {
                                  toast.warning("Marcá primero el plan para poder recomendarlo");
                                } else {
                                  setRecomendadoId(p.id);
                                }
                              }}
                              disabled={hasReco && !isReco}
                              title={
                                isReco ? "Plan recomendado — clic para quitar"
                                : hasReco ? "Solo se puede recomendar un plan por cotización"
                                : !checked ? "Marcá primero el plan"
                                : "Marcar como plan recomendado para el cliente"
                              }
                              className={`p-0.5 rounded transition-all ${
                                isReco ? "text-amber-500 hover:text-amber-600"
                                : hasReco ? "text-slate-200 cursor-not-allowed"
                                : "text-slate-300 hover:text-amber-400 cursor-pointer"
                              }`}
                              data-testid={`cot-plan-reco-${p.id}`}
                            >
                              <Star className={`w-4 h-4 ${isReco ? "fill-amber-400" : ""}`} />
                            </button>
                            <span className="text-[10px] text-slate-400 w-[80px] text-right">{p.coberturas_count}cob · {p.tasas_count}tasa{p.tasas_count !== 1 ? "s" : ""}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">3. Vigencia del enlace (días)</Label>
            <Input type="number" min={1} max={90} value={vigenciaDias} onChange={(e) => setVigenciaDias(e.target.value)} className="h-9 max-w-[120px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={creating || !vehSel || totalSeleccionados === 0} className="bg-[#80BC00] hover:bg-[#6ea700]" data-testid="cot-new-submit">
            {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Generar cotización
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CotizacionDetalle({ cotizacionId, onClose }) {
  const { getAuthHeaders } = useAuth();
  const [doc, setDoc] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!cotizacionId) return;
    fetch(`${API_URL}/api/pcc/cotizaciones/${cotizacionId}`, getAuthHeaders()).then(r => r.json()).then(setDoc);
  }, [cotizacionId, getAuthHeaders]);

  const handleEnviar = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/pcc/cotizaciones/${cotizacionId}/enviar`, { method: "POST", headers: getAuthHeaders().headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success(`Marcada como enviada a ${d.destinatario}`);
      const r2 = await fetch(`${API_URL}/api/pcc/cotizaciones/${cotizacionId}`, getAuthHeaders());
      setDoc(await r2.json());
    } catch (err) { toast.error(err.message); }
    setSending(false);
  };

  const handleDownload = (kind) => {
    const url = `${API_URL}/api/pcc/cotizaciones/${cotizacionId}/${kind}.pdf`;
    const headers = getAuthHeaders().headers;
    fetch(url, { headers }).then(r => r.blob()).then(b => {
      const u = window.URL.createObjectURL(b);
      window.open(u, "_blank");
    });
  };

  if (!cotizacionId) return null;
  const portalUrl = doc?.token ? `${window.location.origin}/pcc-portal/${doc.token}` : "";

  return (
    <Dialog open={Boolean(cotizacionId)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cot-detalle-dialog">
        <DialogHeader><DialogTitle>Detalle de cotización</DialogTitle></DialogHeader>
        {!doc ? <Loader2 className="w-6 h-6 mx-auto animate-spin my-8" /> : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Card className="border-slate-200">
                <CardContent className="p-3 text-xs space-y-1">
                  <p className="font-bold text-slate-800">{doc.vehiculo_snapshot?.empleado_nombre}</p>
                  <p>{doc.vehiculo_snapshot?.cedula} · {doc.vehiculo_snapshot?.email}</p>
                  <p>{doc.vehiculo_snapshot?.marca} {doc.vehiculo_snapshot?.modelo} {doc.vehiculo_snapshot?.anio}</p>
                  <p>Valor: DOP {Number(doc.vehiculo_snapshot?.valor_asegurado || 0).toLocaleString("es-DO")}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-3 text-xs space-y-1">
                  <p>Estado: <Badge>{doc.estado}</Badge></p>
                  <p>Vence: {new Date(doc.expires_at).toLocaleString("es-DO")}</p>
                  {doc.seleccion && <p className="text-emerald-700">Elegida: <b>{doc.seleccion.aseguradora_nombre}{doc.seleccion.plan_nombre ? ` · ${doc.seleccion.plan_nombre}` : ""}</b> · DOP {Number(doc.seleccion.prima_anual).toLocaleString("es-DO")}</p>}
                  {doc.firma && <p className="text-emerald-700 font-bold">✓ Firmada · Folio {doc.firma.folio}</p>}
                </CardContent>
              </Card>
            </div>
            <div>
              <p className="text-xs font-bold mb-1">Opciones cotizadas</p>
              <table className="w-full text-xs border border-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-2 text-left">Aseguradora</th>
                    <th className="p-2 text-left">Plan</th>
                    <th className="p-2 text-right">Tasa</th>
                    <th className="p-2 text-right">Prima anual</th>
                    <th className="p-2 text-right">Deducible</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.opciones?.map((o, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 font-medium">{o.aseguradora_nombre}</td>
                      <td className="p-2 text-slate-600">{o.plan_nombre || "—"}</td>
                      <td className="p-2 text-right">{o.tasa_aplicada ? `${o.tasa_aplicada}%` : "—"}</td>
                      <td className="p-2 text-right font-bold">{o.prima_anual ? `DOP ${o.prima_anual.toLocaleString("es-DO")}` : <span className="text-rose-600 text-[10px]">{o.error}</span>}</td>
                      <td className="p-2 text-right">{o.deducible ? `DOP ${Number(o.deducible).toLocaleString("es-DO")}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {portalUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                <p className="font-bold text-blue-900">Link único del empleado:</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-[10px] bg-white p-1.5 rounded break-all">{portalUrl}</code>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Copiado"); }} className="h-7 text-xs">Copiar</Button>
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-bold mb-1">Bitácora de eventos</p>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {(doc.eventos || []).map((e, i) => {
                  const by = e.by || e.metadata?.by;
                  return (
                    <div key={i} className="text-[10px] border-l-2 border-emerald-400 pl-2 py-1">
                      <span className="font-bold text-emerald-700">{e.evento}</span>
                      <span> · {new Date(e.at).toLocaleString("es-DO")}</span>
                      {by && <span className="text-slate-700"> · por <b>{by}</b></span>}
                      {e.ip && <span className="text-slate-400"> · IP {e.ip}</span>}
                      {e.metadata?.destinatario && <span className="text-slate-500"> · → {e.metadata.destinatario}</span>}
                      {e.metadata?.aseguradora && <span className="text-slate-500"> · {e.metadata.aseguradora}{e.metadata.plan ? ` (${e.metadata.plan})` : ""}</span>}
                      {e.metadata?.folio && <span className="text-emerald-600 font-bold"> · Folio {e.metadata.folio}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => handleDownload("comparativo")} data-testid="cot-pdf-comparativo">
            <Download2 /> Comparativo PDF
          </Button>
          {doc?.firma && (
            <Button variant="outline" onClick={() => handleDownload("constancia")} className="border-emerald-300 text-emerald-700" data-testid="cot-pdf-constancia">
              <Download2 /> Constancia PDF
            </Button>
          )}
          {doc?.estado === "generada" && (
            <Button onClick={handleEnviar} disabled={sending} className="bg-[#193966] hover:bg-[#0F2240] text-white" data-testid="cot-enviar-btn">
              {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Marcar como enviada
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Download2() { return <span className="inline-flex items-center"><FileText className="w-3 h-3 mr-1" /></span>; }

function CotizacionesPlaceholder() {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-12 text-center">
        <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <h3 className="text-lg font-bold text-slate-700">Cotizaciones</h3>
        <p className="text-sm text-slate-500 mt-1">Generá y gestioná las cotizaciones del Portal ALNAP.</p>
      </CardContent>
    </Card>
  );
}
